import { formatMetricLabel } from './formatMetric';

export interface ImportPreviewRow {
  row: number;
  code: string;
  name: string;
  unit: string;
  frequency: string;
  direction: string;
  contractCode: string;
  target: number;
  weight: number;
  expression?: string | null;
  operation?: string;
  unitCode?: string;
  effectiveFrom?: string | null;
}

export interface ImportPreviewError { row: number; code: string; message: string; }
export interface ImportPreviewResult {
  batchId: string;
  validRows: number;
  errorRows: number;
  rows: { definitions: ImportPreviewRow[]; errors: ImportPreviewError[] };
}

export interface ImportTableRow { row: number | 'Lot'; code: string; name: string; details: string; target: string | number; valid: boolean; status: string; }

/** Builds safe text-only table rows and retains the source workbook row numbers. */
export function importTableRows(preview: ImportPreviewResult): ImportTableRow[] {
  const { definitions, errors } = preview.rows ?? { definitions: [], errors: [] };
  const errorsByRow = new Map<number, ImportPreviewError[]>();
  for (const error of errors) errorsByRow.set(error.row, [...(errorsByRow.get(error.row) ?? []), error]);
  const rows: ImportTableRow[] = definitions.map((definition) => {
    const rowErrors = errorsByRow.get(definition.row) ?? [];
    return {
      row: definition.row,
      code: definition.code,
      name: definition.name,
      details: `${definition.operation ? ({ create: "Création", version: "Nouvelle version", attach: "Rattachement", update: "Configuration future", archive: "Archivage" } as Record<string,string>)[definition.operation] + " · " : ""}${definition.unitCode ? definition.unitCode + " · " : ""}${definition.effectiveFrom ? definition.effectiveFrom + " · " : ""}${definition.unit} · ${definition.frequency} · ${definition.direction} · ${definition.contractCode}${definition.expression ? ` · Formule : ${definition.expression}` : ''}`,
      target: formatMetricLabel(definition.target, definition.unit),
      valid: rowErrors.length === 0,
      status: rowErrors.map((error) => error.message).join(' · ') || 'Prêt',
    };
  });
  for (const [number, rowErrors] of errorsByRow) {
    if (number === 0 || definitions.some((definition) => definition.row === number)) continue;
    rows.push({ row: number, code: '—', name: 'Ligne invalide', details: '—', target: '—', valid: false, status: rowErrors.map((error) => error.message).join(' · ') });
  }
  for (const error of errorsByRow.get(0) ?? []) {
    rows.push({ row: 'Lot', code: '—', name: error.message, details: '—', target: '—', valid: false, status: 'À corriger' });
  }
  return rows.sort((left, right) => left.row === 'Lot' ? 1 : right.row === 'Lot' ? -1 : Number(left.row) - Number(right.row));
}

export function importSourceLineCount(preview: ImportPreviewResult): number {
  return new Set([
    ...preview.rows.definitions.map((definition) => definition.row),
    ...preview.rows.errors.filter((error) => error.row > 0).map((error) => error.row),
  ]).size;
}
